import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import "../styles/Auth.css";
import "../styles/NewAuth.css";
import MainFooter from "../components/MainFooter";
import { API_BASE_URL } from "../config";

const LOGO_SRC = "/eventfork_logo.svg";

const SignIn = ({ setUser }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/auth/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const data = await response.json();
      if (response.ok) {
        if (data.token) localStorage.setItem("token", data.token);
        if (data.user) {
          localStorage.setItem("user", JSON.stringify(data.user));
          setUser(data.user);
        }
        navigate("/");
      } else {
        setError(data.message || "Login failed");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setResetEmail("");
    setResetMessage("");
    setResetError("");
  };

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains("forgot-modal-overlay")) closeModal();
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage("");
    setResetError("");
    try {
      const response = await fetch(`${API_BASE_URL}/reset_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await response.json();
      if (response.ok) {
        setResetMessage(data.message || "Reset link sent! Check your email.");
      } else {
        setResetError(data.message || "Failed to send reset link.");
      }
    } catch (err) {
      setResetError("Network error. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-wrapper">

          {/* ── LEFT BRANDING PANEL ── */}
          <div className="auth-branding">
            <div className="container">
              <h1>
                Discover <em>extraordinary</em> events near you
              </h1>
              <p>
                Connect with your city's best experiences — conferences, meetups,
                Exhibition,marathon and more. Your next unforgettable moment is one
                click away.
              </p>
              <ul className="features-list">
                <li>
                  <i className="fas fa-check" />
                  Personalized event recommendations
                </li>
                <li>
                  <i className="fas fa-check" />
                  One-click ticket purchasing
                </li>
                <li>
                  <i className="fas fa-check" />
                  Create and manage your own events
                </li>
                <li>
                  <i className="fas fa-check" />
                  Connect with event enthusiasts
                </li>
              </ul>

              <div className="branding-tags">
                {["Music", "Tech", "Food & Drink", "Art", "Networking", "Wellness"].map((tag) => (
                  <span className="branding-tag" key={tag}>{tag}</span>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT FORM PANEL ── */}
          <div className="auth-form-container">
            <div className="form-header">
              <img src={LOGO_SRC} alt="EventFork" />
              <h2>Welcome back</h2>
              <p>Sign in to continue exploring events.</p>
            </div>

            <div className="auth-toggle">
              <Link to="/signin" className="toggle-btn active">Sign In</Link>
              <Link to="/signup" className="toggle-btn">Sign Up</Link>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <div className="password-input-container">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    id="password"
                    name="password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="password-toggle password-toggle-span"
                    onClick={() => setPasswordVisible((v) => !v)}
                  >
                    {passwordVisible ? "Hide" : "Show"}
                  </button>
                </div>
              </div>

              <div className="remember-forgot">
                <div className="remember-me">
                  <input type="checkbox" id="rememberMe" />
                  <label htmlFor="rememberMe">Remember me</label>
                </div>
                <button type="button" className="forgot-password" onClick={openModal}>
                  Forgot Password?
                </button>
              </div>

              <button type="submit" className="btn btn-primary1" disabled={loading}>
                {loading ? "Signing In…" : "Sign In"}
              </button>
            </form>

            <div className="auth-redirect">
              Don't have an account?{" "}
              <Link to="/signup" className="auth-link">Sign Up</Link>
            </div>
          </div>
        </div>
      </div>

      {/* ── FORGOT PASSWORD MODAL ── */}
      {isModalOpen && (
        <div className="forgot-modal-overlay" onClick={handleOverlayClick}>
          <div className="forgot-modal">
            <div className="forgot-modal-content">
              <div className="forgot-modal-header">
                <h3>Reset Password</h3>
                <button className="close-modal" onClick={closeModal} aria-label="Close">×</button>
              </div>
              <p className="text">
                Enter your email and we'll send you a link to reset your password.
              </p>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <form onSubmit={handleResetPassword}>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="you@example.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                  {resetMessage && (
                    <div className="success-message" style={{ marginTop: 14 }}>{resetMessage}</div>
                  )}
                  {resetError && (
                    <div className="error-message" style={{ marginTop: 14 }}>{resetError}</div>
                  )}
                  <button type="submit" className="btn btn-primary" disabled={resetLoading}>
                    {resetLoading ? "Sending…" : "Send Reset Link"}
                  </button>
                </form>
              </div>
              <button className="back-btn" onClick={closeModal}>
                ← Back to Sign In
              </button>
            </div>
          </div>
        </div>
      )}

      <MainFooter />
    </div>
  );
};

export default SignIn;
